import { useState, useCallback, useMemo } from 'react';
import authService, { ActivatedPack, ActivatedPacksResponse, Store as StoreType } from '../services/authService';
import { buildProcessedTicketDataList } from '../utils/ticketFactory';

interface UseActivatedPacksProps {
  storeId: string;
  selectedLocationId?: string;
  stores?: StoreType[]; // Add stores as optional prop
}

export const useActivatedPacks = ({ storeId, selectedLocationId, stores = [] }: UseActivatedPacksProps) => {
  const [packsData, setPacksData] = useState<ActivatedPacksResponse | null>(null);
  const [storeData, setStoreData] = useState<StoreType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Process raw API data into optimized format
  const processedPacks = useMemo(() => {
    if (!packsData?.items) return [];
    return buildProcessedTicketDataList(packsData.items, {
      format: packsData.barcode_format,
      barcodeLength: packsData.barcode_length,
      scannerFormats: packsData.barcode_scanner_formats,
      direction: packsData.direction,
      regionStartingTicketNumber: packsData.region_starting_ticket_number,
    });
  }, [packsData]);

  const loadStoreAndPacks = useCallback(async () => {
    if (!storeId) return;

    try {
      setLoading(true);
      setError(null);

      // Use stores from props instead of making API call
      const store = stores.find(s => s.id === storeId);
      setStoreData(store || null);

      // Save store data to localStorage for use in payout APIs
      if (store) {
        localStorage.setItem('storeData', JSON.stringify(store));
      }

      // Load activated packs
      const data = await authService.getActivatedPacks(storeId, selectedLocationId || undefined);
      setPacksData(data);
    } catch (err) {
      console.error('Error loading store and packs:', err);
      setError('Failed to load store and packs data');
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId, storeId, stores]);

  const addNewPack = useCallback((newPack: ActivatedPack) => {
    if (!packsData) return;

    setPacksData(prev => ({
      ...prev!,
      barcode_format: prev?.barcode_format,
      barcode_length: prev?.barcode_length,
      barcode_scanner_formats: prev?.barcode_scanner_formats,
      direction: prev?.direction,
      items: [newPack, ...prev!.items],
      total: prev!.total + 1
    }));
  }, [packsData]);

  const removePack = useCallback((packId: string) => {
    if (!packsData) return;

    setPacksData(prev => ({
      ...prev!,
      items: prev!.items.filter(pack => pack.pack_id !== packId),
      total: prev!.total - 1
    }));
  }, [packsData]);

  return {
    packsData,
    storeData,
    processedPacks,
    loading,
    error,
    setError,
    loadStoreAndPacks,
    addNewPack,
    removePack,
  };
}; 
